// ============================================
// BADGE DE STATUT — couleur, icône et libellé
// ============================================
// Un seul badge de statut pour tous les écrans (liste, cartes, fiche,
// recherche, clients, téléphonie, analyses). L'icône remplace la pastille :
// la couleur n'est jamais le seul repère, le libellé reste écrit, et
// l'infobulle dit ce que le statut signifie.

import React from 'react';
import { Inbox, Wrench, Hourglass, RotateCcw, AlertTriangle, CheckCircle2, Archive, CircleDashed, type LucideIcon } from 'lucide-react';
import { TicketStatus } from '../../types';
import { STATUS_LABELS } from '../labels';
import { CLASSES_STATUT, COULEUR_STATUT } from '../format';
import { explicationStatut } from '../statuts';

/** Une icône par statut, la même partout (badge, parcours, puces de filtre). */
export const ICONE_STATUT: Record<TicketStatus, LucideIcon> = {
  [TicketStatus.OPEN]: Inbox,
  [TicketStatus.IN_PROGRESS]: Wrench,
  [TicketStatus.WAITING_CUSTOMER]: Hourglass,
  [TicketStatus.REOPENED]: RotateCcw,
  [TicketStatus.ESCALATED]: AlertTriangle,
  [TicketStatus.RESOLVED]: CheckCircle2,
  [TicketStatus.CLOSED]: Archive,
};

/** Libellé d'un statut, ou la valeur brute si elle est inconnue. */
export const libelleStatut = (status: TicketStatus | string): string =>
  STATUS_LABELS[status as TicketStatus] ?? String(status ?? '');

/** Icône d'un statut (cercle pointillé si le statut est inconnu). */
export const IconeStatut: React.FC<{ status: TicketStatus | string; className?: string; strokeWidth?: number; style?: React.CSSProperties }> = ({ status, className = 'w-3.5 h-3.5', strokeWidth, style }) => {
  const I = ICONE_STATUT[status as TicketStatus] ?? CircleDashed;
  return <I aria-hidden="true" className={`flex-shrink-0 ${className}`} strokeWidth={strokeWidth} style={style} />;
};

export const BadgeStatut: React.FC<{
  status: TicketStatus | string;
  /** `md` : en-tête de la fiche. */
  taille?: 'sm' | 'md';
  className?: string;
}> = ({ status, taille = 'sm', className = '' }) => {
  const libelle = libelleStatut(status);
  const explication = explicationStatut(status);
  const couleurs = CLASSES_STATUT[status as TicketStatus] ?? 'bg-brume text-graphite border-trait';
  const dimensions = taille === 'md' ? 'gap-1.5 px-3 py-1 text-sm' : 'gap-1 px-2.5 py-0.5 text-xs';
  return (
    <span
      data-statut={status}
      title={explication ? `${libelle} : ${explication}` : libelle}
      className={`inline-flex items-center whitespace-nowrap rounded-full border font-semibold ${dimensions} ${couleurs} ${className}`}
    >
      <IconeStatut status={status} className={taille === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      <span>{libelle}</span>
    </span>
  );
};

/** Couleur franche d'un statut (gris si inconnu). */
export const couleurStatut = (status: TicketStatus | string): string =>
  COULEUR_STATUT[status as TicketStatus] ?? '#64748B';
